// Calendar helpers. Days are Europe/London calendar days as YYYY-MM-DD strings
// (engine Day); nothing here needs a time zone because the API sends days.
import type { Day, Half } from "./types";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

const at = (day: Day) => new Date(`${day}T12:00:00Z`);

/** "Mon" */
export const weekday = (day: Day) => WEEKDAYS[at(day).getUTCDay()];
/** "Monday" */
export const weekdayLong = (day: Day) => LONG[at(day).getUTCDay()];
/** "2 Nov" */
export const dayMonth = (day: Day) => `${at(day).getUTCDate()} ${MONTHS[at(day).getUTCMonth()]}`;
/** "Mon 2 Nov" */
export const shortDay = (day: Day) => `${weekday(day)} ${dayMonth(day)}`;

export function addDays(day: Day, n: number): Day {
  const d = at(day);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** The Monday of the week holding `day`. */
export function mondayOf(day: Day): Day {
  const wd = at(day).getUTCDay();
  return addDays(day, wd === 0 ? -6 : 1 - wd);
}

/** "WC 2 Nov" as the floor says it. */
export const weekLabel = (weekStart: Day) => `Week of ${dayMonth(weekStart)}`;

export const HALVES: Half[] = ["AM", "PM"];
/** Half-day times as the Sheets have them. */
export const HALF_TIMES: Record<Half, { start: number; end: number; label: string }> = {
  AM: { start: 9 * 60, end: 12 * 60 + 30, label: "09:00–12:30" },
  PM: { start: 13 * 60, end: 17 * 60, label: "13:00–17:00" }
};

/** Index of a slot in the week (Mon AM = 0 … Fri PM = 9). */
export function slotIndex(days: Day[], day: Day, half: Half): number {
  return days.indexOf(day) * 2 + (half === "AM" ? 0 : 1);
}

/** True when (day, half) comes before `ref` in the week. */
export function slotBefore(day: Day, half: Half, ref: { day: Day; half: Half } | null): boolean {
  if (!ref) return false;
  if (day !== ref.day) return day < ref.day;
  return half === "AM" && ref.half === "PM";
}

/** "16:40" from minutes after midnight. */
export const clock = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;

/** "16:40" from an ISO timestamp (shown as the floor's local time the API sent). */
export const timeOf = (iso: string | null | undefined) => (iso ? iso.slice(11, 16) : "");

/** "3 Nov 16:40" */
export const stamp = (iso: string | null | undefined) => (iso ? `${dayMonth(iso.slice(0, 10))} ${iso.slice(11, 16)}` : "");
